import { Main } from "@/app/layout/Main";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import { Badge, Button, Card, CardContent } from "@devhop/ui";
import { format } from "date-fns";
import {
  ArrowLeft,
  ClipboardList,
  ExternalLink,
  StickyNote,
} from "lucide-react";
import type { InternalAuditRecordDetailDTO } from "../api/client";

function overallBadge(result: "pass" | "fail" | "needs_improvement") {
  if (result === "pass")
    return (
      <Badge variant="secondary" className="bg-emerald-600/15 text-emerald-800">
        ຜ່ານ
      </Badge>
    );
  if (result === "fail") return <Badge variant="destructive">ບໍ່ຜ່ານ</Badge>;
  return (
    <Badge variant="secondary" className="bg-amber-500/15 text-amber-900">
      ຕ້ອງປັບປຸງ
    </Badge>
  );
}

function itemResultBadge(result: "pass" | "fail" | "needs_improvement") {
  return overallBadge(result);
}

export function InternalAuditRecordDetailView({
  data,
  title,
  onBack,
}: {
  data: InternalAuditRecordDetailDTO;
  title: string;
  onBack: () => void;
}) {
  const { record, items, attachments } = data;

  return (
    <Main className="flex flex-col gap-6 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-8 w-8 text-primary" />
          <h1 className="font-semibold text-2xl">{title}</h1>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 gap-1"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" />
          ກັບ
        </Button>
      </div>

      <Card className="border-border py-0">
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ແຜນຕວດ
              </p>
              <p className="font-semibold text-lg">{record.planName}</p>
              <p className="mt-1 text-muted-foreground text-sm">
                {record.auditTypeName}
                {record.qcStandardName ? ` · ${record.qcStandardName}` : ""} ·{" "}
                {record.departmentName}
              </p>
            </div>
            {overallBadge(record.overallResult)}
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">ວັນທີຕວດ</dt>
              <dd className="font-medium">
                {format(new Date(record.auditDate), "yyyy-MM-dd HH:mm")}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">ຜູ້ຕວດ</dt>
              <dd className="font-medium">
                {record.auditorName?.trim() || record.auditorId}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">ຕຳແໜ່ງຜູ້ກວດ (ແຜນ)</dt>
              <dd className="font-medium">
                {record.auditorPositionTitle?.trim() || "—"}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">ຂອບເຂດການຕວດ</dt>
              <dd className="mt-0.5 whitespace-pre-wrap leading-relaxed">
                {record.auditScope}
              </dd>
            </div>
            {record.summary?.trim() ? (
              <div className="sm:col-span-2">
                <dt className="flex items-center gap-1.5 text-muted-foreground">
                  <StickyNote className="size-3.5" aria-hidden />
                  ສະຫຼຸບ
                </dt>
                <dd className="mt-0.5 whitespace-pre-wrap leading-relaxed">
                  {record.summary}
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="text-muted-foreground">ສະຖານະບັນທຶກ</dt>
              <dd className="font-medium">
                {record.status === "completed" ? "ສຳເລັດ" : "ຮ່າງ"}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {attachments.length > 0 ? (
        <section className="space-y-3">
          <h2 className="font-medium text-lg">ຮູບປະກອບ</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {attachments.map((att) => {
              const href = resolveImageSrc(att.fileKey);
              return (
                <Card
                  key={att.id}
                  className="overflow-hidden border-border/80 py-0 shadow-sm"
                >
                  <div className="relative aspect-video w-full bg-muted/40">
                    <AppImage
                      src={att.fileKey}
                      alt=""
                      className="size-full"
                      fit="contain"
                    />
                  </div>
                  {href ? (
                    <CardContent className="p-3">
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-primary text-sm hover:underline"
                      >
                        <ExternalLink className="size-3.5" aria-hidden />
                        ເປີດຮູບເຕັມ
                      </a>
                    </CardContent>
                  ) : null}
                </Card>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="space-y-3">
        <h2 className="font-medium text-lg">ລາຍການຕວດ</h2>
        <div className="flex flex-col gap-3">
          {items.map((it, idx) => (
            <Card
              key={it.id}
              className="overflow-hidden border-border/80 py-0 shadow-sm"
            >
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 gap-3">
                    <span
                      className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted font-semibold text-muted-foreground text-xs tabular-nums"
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold leading-snug">{it.title}</p>
                      {it.description?.trim() ? (
                        <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
                          {it.description}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {itemResultBadge(it.result)}
                </div>
                {it.notes?.trim() ? (
                  <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-sm">
                    <p className="text-muted-foreground text-xs">ຫມາຍເຫດ</p>
                    <p className="mt-0.5 whitespace-pre-wrap">{it.notes}</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </Main>
  );
}
